import React, { useEffect } from 'react';
import { ArrowLeftIcon, CheckIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { contact, founder } from '../data/content';
import { track } from '../utils/tracking';
import { fireLeadOnPageView } from '../utils/leadEvent';
const nextSteps = [{
  title: 'We review what you sent',
  body: 'Phil and the NEXT leadership team read every submission personally. Nothing goes into a mass drip.'
}, {
  title: 'You hear from us within one business day',
  body: 'A short text or call to find a time that actually works around your showings and closings.'
}, {
  title: 'A private, no-pressure conversation',
  body: 'Your business, your goals, and where better systems, marketing, or support could move the needle.'
}];

/** Confirmation page for form submissions. Fires the Meta `Lead` conversion on view. */
export function ThankYou() {
  useEffect(() => fireLeadOnPageView(), []);
  return <div className="flex min-h-screen w-full flex-col bg-white" data-id="element-232" data-mp="285w64iiovd1l">
      <header className="w-full border-b border-line" data-id="element-233" data-mp="2877ekvn2x8z1">
        <div className="mx-auto flex h-14 max-w-content items-center px-4 md:h-[68px] md:px-8" data-id="element-234" data-mp="236jms7r10o3r">
          <a href="/" className="flex items-center" aria-label="HomeSmart NEXT home" data-id="element-235" data-mp="1y6ybqyyl0pao">
            <Logo data-id="element-236" data-mp="1u6eii6h4q5tt" />
          </a>
        </div>
      </header>

      <main className="mx-auto w-full max-w-content flex-1 px-4 py-14 md:px-8 md:py-20" data-id="element-237" data-mp="1u87nzig0ak08">
        <div className="max-w-2xl" data-id="element-238" data-mp="2qvm2vb60i8w5">
          <div data-id="element-239" data-mp="2lxaw59b0kei5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft" data-id="element-240" data-mp="2lwsq5kyjulx1">
              <CheckIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-241" data-mp="2h5srob9s7vsf" />
            </span>

            <h1 className="mt-5 font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[40px] lg:text-[46px]" data-id="element-242" data-mp="2d5ijqt2fivax">
              Your request is in.
              <span className="block text-accent" data-id="element-243" data-mp="34wezc1fvje1w">Let&rsquo;s find a time to talk.</span>
            </h1>

            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-600 md:mt-6 md:text-[17px]" data-id="element-244" data-mp="12ywbhnob98xv">
              Thanks for reaching out to HomeSmart NEXT. This is a private conversation about your business, not a
              recruiting pitch and not a mailing list.
            </p>

            <ol className="mt-8 space-y-5 border-l border-line pl-6" data-id="element-245" data-mp="y7mq4z9sorrs">
              {nextSteps.map(step => <li key={step.title} className="relative" data-id="element-246" data-mp="k7lq1m1ctodd">
                  <span className="absolute -left-[26px] top-[7px] h-2 w-2 rounded-full bg-accent" aria-hidden="true" data-id="element-247" data-mp="ka7u3bp1dlqu" />
                  <p className="font-display text-[15px] font-semibold text-ink md:text-[16px]" data-id="element-248" data-mp="abiejpkm4ddr">{step.title}</p>
                  <p className="mt-1 text-[14px] leading-relaxed text-ink-600" data-id="element-249" data-mp="fb4qlvopfnkz">{step.body}</p>
                </li>)}
            </ol>

            <div className="mt-9 flex flex-col gap-3 border-t border-line pt-6 sm:flex-row sm:gap-8" data-id="element-250" data-mp="29nl2pd46lvxr">
              <a href={`tel:${contact.phone}`} onClick={() => track('phone_click', {
              location: 'thank_you'
            })} className="flex items-center gap-2 text-[15px] font-medium text-ink transition-colors duration-150 ease-smooth hover:text-accent" data-id="element-251" data-mp="3u4c7xlrhtn0u">
                <PhoneIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-252" data-mp="2yj870o05qoa" />
                Call or text: {contact.phone}
              </a>
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-[15px] font-medium text-ink transition-colors duration-150 ease-smooth hover:text-accent" data-id="element-253" data-mp="gsagy6twt6iy">
                <MailIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-254" data-mp="lpk2hbe6gy1i" />
                {contact.email}
              </a>
            </div>

            <a href="/" className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium text-ink-600 transition-colors duration-150 ease-smooth hover:text-ink" data-id="element-255" data-mp="2sga3lbswppct">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-256" data-mp="2oxkpshpbsm7s" />
              Back to HomeSmart NEXT
            </a>
          </div>

          <figure className="mt-10 flex flex-col gap-5 border-t border-line pt-8 sm:flex-row sm:items-start sm:gap-6" data-id="element-257" data-mp="2yw9xjhnicqab">
            <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-canvas md:h-[104px] md:w-[104px]" data-id="element-258" data-mp="33vd4j7n1t6kx">
              <img src={founder.image} alt={`${founder.name}, ${founder.title} of HomeSmart NEXT`} loading="eager" decoding="async" className="absolute left-[-33%] top-0 w-[190%] max-w-none" data-id="element-259" data-mp="268nt676ghhme" />
            </div>
            <div data-id="element-260" data-mp="2kxse3w4mxzy7">
              <blockquote className="font-display text-[16px] leading-[1.5] tracking-[-0.01em] text-ink md:text-[18px]" data-id="element-261" data-mp="2kybx7g60uudt">
                &ldquo;I read every one of these myself. Looking forward to hearing about your business and where you
                want to take it.&rdquo;
              </blockquote>
              <figcaption className="mt-3 text-[13px] font-medium text-ink-400" data-id="element-262" data-mp="9ipljqy9pfx4">
                <span className="text-ink" data-id="element-263" data-mp="5k01xgmmz251">{founder.name}</span> — {founder.title}, {founder.company}
              </figcaption>
            </div>
          </figure>
        </div>
      </main>

      <footer className="w-full border-t border-line" data-id="element-264" data-mp="3rpna0ljuxbzi">
        <div className="mx-auto max-w-content px-4 py-6 text-[12px] leading-relaxed text-ink-400 md:px-8" data-id="element-265" data-mp="3mr3m4flpdq4i">
          <p data-id="element-266" data-mp="3i03mh2l78tyb">HomeSmart NEXT is a real estate team affiliated with HomeSmart.</p>
          <p className="mt-2" data-id="element-267" data-mp="3dz1oep30qnxx">© {new Date().getFullYear()} HomeSmart NEXT. All rights reserved.</p>
        </div>
      </footer>
    </div>;
}